import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import {
  useLiveSession,
  useTicket,
  useUpdateTicket,
  useUsers,
  useCompanyWebsites,
  useTicketAttachments,
  useUploadAttachment,
} from '@/lib/adminApi';
import type { TicketSummary, TicketPriority, TicketCategory } from '@ventin/shared';
import {
  PriorityBadge,
  CategoryBadge,
  PRIORITY_OPTIONS,
  CATEGORY_OPTIONS,
} from '@/components/tickets/ticketMeta';
import { CommentThread } from '@/components/tickets/CommentThread';
import { ClearCacheButton } from '@/components/websites/ClearCacheButton';
import { ScreenshotCaptureFlow } from '@/components/screenshot/ScreenshotCaptureFlow';
import { AttachmentGallery } from '@/components/screenshot/gallery/AttachmentGallery';
import type { ScreenshotResult } from '@/components/screenshot/types';
import { TicketBillableDialog } from './TicketBillableDialog';
import { KbSuggestPanel } from './KbSuggestPanel';
import { SessionTicketsPanel } from './SessionTicketsPanel';

const selectClass =
  'h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring';

/**
 * TicketDetailPage — /admin/tickets/:id (TKT-03, TKT-04, TKT-06).
 *
 * Shows ticket detail with:
 * - Billable toggle row → opens TicketBillableDialog (requires reason, TKT-03)
 * - usesIncrements checkbox → updates immediately (TKT-04)
 * - Status select → updates immediately
 * - KbSuggestPanel in right column (TKT-06)
 */
export default function TicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, isError } = useTicket(id ?? '');
  const updateMut = useUpdateTicket();
  const { data: liveSessionData } = useLiveSession();
  const liveSession = liveSessionData?.session ?? null;

  // Assignee candidates = staff only (ADMIN/SUPPORT); website options are the
  // ticket company's registered sites.
  const { data: usersData } = useUsers();
  const assignableUsers = (usersData?.users ?? []).filter(
    (u) => u.role === 'ADMIN' || u.role === 'SUPPORT',
  );
  const { data: websitesData } = useCompanyWebsites(data?.ticket.companyId);
  const websites = websitesData?.websites ?? [];

  const { data: attachmentsData } = useTicketAttachments(id ?? '');
  const attachments = attachmentsData?.attachments ?? [];
  const uploadMut = useUploadAttachment();

  const [billableDialogOpen, setBillableDialogOpen] = useState(false);

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto px-6 py-8">
        <p className="text-sm text-muted-foreground">Loading ticket…</p>
      </div>
    );
  }

  if (isError || !data?.ticket) {
    return (
      <div className="max-w-6xl mx-auto px-6 py-8">
        <Alert variant="destructive">
          <AlertDescription>Couldn't load ticket — try again.</AlertDescription>
        </Alert>
      </div>
    );
  }

  const ticket = data.ticket;
  const linkedWebsite = websites.find((w) => w.id === ticket.websiteId);

  async function handleStatusChange(newStatus: TicketSummary['status']) {
    if (!id) return;
    try {
      await updateMut.mutateAsync({ id, patch: { status: newStatus } });
      toast.success('Status updated');
    } catch {
      toast.error("Couldn't update status — try again");
    }
  }

  async function handleUsesIncrementsChange(checked: boolean) {
    if (!id) return;
    try {
      await updateMut.mutateAsync({ id, patch: { usesIncrements: checked } });
      toast.success('Increments setting updated');
    } catch {
      toast.error("Couldn't update — try again");
    }
  }

  async function handleBillableSubmit(payload: { isBillable: boolean; billableReason: string }) {
    if (!id) return;
    try {
      await updateMut.mutateAsync({ id, patch: payload });
      toast.success('Billable status updated');
      setBillableDialogOpen(false);
    } catch {
      toast.error("Couldn't update — try again");
    }
  }

  async function handlePriorityChange(priority: TicketPriority) {
    if (!id) return;
    try {
      await updateMut.mutateAsync({ id, patch: { priority } });
      toast.success('Priority updated');
    } catch {
      toast.error("Couldn't update priority — try again");
    }
  }

  async function handleCategoryChange(category: TicketCategory) {
    if (!id) return;
    try {
      await updateMut.mutateAsync({ id, patch: { category } });
      toast.success('Category updated');
    } catch {
      toast.error("Couldn't update category — try again");
    }
  }

  // Empty select value clears the field (assignedToId/websiteId are nullable).
  async function handleAssigneeChange(assignedToId: string) {
    if (!id) return;
    try {
      await updateMut.mutateAsync({ id, patch: { assignedToId: assignedToId || null } });
      toast.success('Assignee updated');
    } catch {
      toast.error("Couldn't update assignee — try again");
    }
  }

  async function handleWebsiteChange(websiteId: string) {
    if (!id) return;
    try {
      await updateMut.mutateAsync({ id, patch: { websiteId: websiteId || null } });
      toast.success('Website updated');
    } catch {
      toast.error("Couldn't update website — try again");
    }
  }

  async function handleAttachScreenshot(result: ScreenshotResult) {
    if (!id) return;
    try {
      await uploadMut.mutateAsync({ id, result });
      toast.success('Screenshot attached');
    } catch {
      toast.error("Couldn't attach screenshot — try again");
    }
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/admin/tickets"
          className="text-sm text-muted-foreground hover:text-foreground mb-2 inline-block"
        >
          ← Back to Tickets
        </Link>
        <div className="flex items-start justify-between gap-4 mt-1">
          <div>
            <h2 className="text-2xl font-semibold leading-[1.3]">{ticket.title}</h2>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <TicketStatusChip status={ticket.status} />
              <PriorityBadge priority={ticket.priority} />
              <CategoryBadge category={ticket.category} />
              <span className="text-sm text-muted-foreground">{ticket.source}</span>
            </div>
          </div>
          {/* Status select */}
          <select
            aria-label="Change status"
            className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            value={ticket.status}
            onChange={(e) => handleStatusChange(e.target.value as TicketSummary['status'])}
          >
            <option value="OPEN">Open</option>
            <option value="IN_PROGRESS">In Progress</option>
            <option value="WAITING_CLIENT">Waiting Client</option>
            <option value="RESOLVED">Resolved</option>
            <option value="CLOSED">Closed</option>
          </select>
        </div>
      </div>

      {/* Two-column grid: detail card + KB panel */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main detail card */}
        <div className="lg:col-span-2 space-y-6">
          {/* Description */}
          <section className="rounded-md border bg-background p-5">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
              Description
            </h3>
            <p className="text-sm whitespace-pre-wrap">{ticket.description}</p>
          </section>

          {/* Metadata */}
          <section className="rounded-md border bg-background p-5 space-y-3 text-sm">
            <MetaRow label="Company" value={ticket.companyId} />
            <MetaRow label="Source" value={ticket.source} />
            <MetaRow label="Created" value={new Date(ticket.createdAt).toLocaleDateString()} />
            {ticket.resolvedAt && (
              <MetaRow label="Resolved" value={new Date(ticket.resolvedAt).toLocaleDateString()} />
            )}
          </section>

          {/* Attributes: priority / category / assignee / website */}
          <section className="rounded-md border bg-background p-5 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div className="space-y-1.5">
              <label htmlFor="ticket-priority" className="font-medium">
                Priority
              </label>
              <select
                id="ticket-priority"
                aria-label="Priority"
                className={cn(selectClass, 'w-full')}
                value={ticket.priority}
                disabled={updateMut.isPending}
                onChange={(e) => handlePriorityChange(e.target.value as TicketPriority)}
              >
                {PRIORITY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="ticket-category" className="font-medium">
                Category
              </label>
              <select
                id="ticket-category"
                aria-label="Category"
                className={cn(selectClass, 'w-full')}
                value={ticket.category}
                disabled={updateMut.isPending}
                onChange={(e) => handleCategoryChange(e.target.value as TicketCategory)}
              >
                {CATEGORY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="ticket-assignee" className="font-medium">
                Assignee
              </label>
              <select
                id="ticket-assignee"
                aria-label="Assignee"
                className={cn(selectClass, 'w-full')}
                value={ticket.assignedToId ?? ''}
                disabled={updateMut.isPending}
                onChange={(e) => handleAssigneeChange(e.target.value)}
              >
                <option value="">Unassigned</option>
                {assignableUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.fullName || u.email}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <label htmlFor="ticket-website" className="font-medium">
                Website
              </label>
              <select
                id="ticket-website"
                aria-label="Website"
                className={cn(selectClass, 'w-full')}
                value={ticket.websiteId ?? ''}
                disabled={updateMut.isPending || websites.length === 0}
                onChange={(e) => handleWebsiteChange(e.target.value)}
              >
                <option value="">{websites.length === 0 ? 'No websites' : 'No website'}</option>
                {websites.map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.label || w.url}
                  </option>
                ))}
              </select>
              {linkedWebsite?.mainwpSiteId && (
                <ClearCacheButton
                  websiteId={linkedWebsite.id}
                  companyId={ticket.companyId}
                  cacheLastClearedAt={linkedWebsite.cacheLastClearedAt}
                  className="pt-1"
                />
              )}
            </div>
          </section>

          {/* Billable + usesIncrements */}
          <section className="rounded-md border bg-background p-5 space-y-4 text-sm">
            {/* Billable row */}
            <div className="flex items-center justify-between">
              <div>
                <span className="font-medium">Billable: </span>
                <span>{ticket.isBillable ? 'Yes' : 'No'}</span>
                {ticket.billableReason && (
                  <span className="ml-2 text-muted-foreground">({ticket.billableReason})</span>
                )}
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setBillableDialogOpen(true)}
                disabled={updateMut.isPending}
              >
                Change
              </Button>
            </div>

            {/* usesIncrements row */}
            <div className="flex items-center gap-3">
              <input
                id="uses-increments"
                type="checkbox"
                className="h-4 w-4 rounded border-input"
                checked={ticket.usesIncrements}
                onChange={(e) => handleUsesIncrementsChange(e.target.checked)}
                disabled={updateMut.isPending}
              />
              <label htmlFor="uses-increments" className="font-medium select-none cursor-pointer">
                Uses increments
              </label>
              <span className="text-muted-foreground text-xs">
                (Time billed in 20-minute increments)
              </span>
            </div>
          </section>

          {/* Screenshots: direct attach + gallery with metadata (admin) */}
          <section className="rounded-md border bg-background p-5 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Screenshots
              </h3>
              <ScreenshotCaptureFlow
                onComplete={handleAttachScreenshot}
                disabled={uploadMut.isPending}
              />
            </div>
            {uploadMut.isPending && (
              <p className="text-xs text-muted-foreground">Uploading screenshot…</p>
            )}
            <AttachmentGallery attachments={attachments} showMetadata />
          </section>

          {/* Comment thread (public + internal notes) */}
          {id && <CommentThread ticketId={id} canPostInternal />}
        </div>

        {/* Right column: SessionTicketsPanel (when live) + KB suggestions */}
        <div className="lg:col-span-1 space-y-4">
          {liveSession?.state && liveSession.state !== 'STOPPED' && id && (
            <SessionTicketsPanel session={liveSession} currentTicketId={id} />
          )}
          <KbSuggestPanel ticket={ticket} />
        </div>
      </div>

      <TicketBillableDialog
        open={billableDialogOpen}
        current={{ isBillable: ticket.isBillable, billableReason: ticket.billableReason }}
        onSubmit={handleBillableSubmit}
        onCancel={() => setBillableDialogOpen(false)}
        busy={updateMut.isPending}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <span className="font-medium w-24 shrink-0">{label}:</span>
      <span className="text-muted-foreground">{value}</span>
    </div>
  );
}

const STATUS_STYLES: Record<TicketSummary['status'], { bg: string; dot: string; label: string }> = {
  OPEN: { bg: 'bg-blue-50 text-blue-700', dot: 'bg-blue-500', label: 'Open' },
  IN_PROGRESS: { bg: 'bg-yellow-50 text-yellow-700', dot: 'bg-yellow-500', label: 'In Progress' },
  WAITING_CLIENT: { bg: 'bg-orange-50 text-orange-700', dot: 'bg-orange-400', label: 'Waiting' },
  RESOLVED: { bg: 'bg-green-50 text-green-700', dot: 'bg-green-500', label: 'Resolved' },
  CLOSED: { bg: 'bg-[#F1F1F3] text-[#4B4B52]', dot: 'bg-[#8A8A94]', label: 'Closed' },
};

function TicketStatusChip({ status }: { status: TicketSummary['status'] }) {
  const s = STATUS_STYLES[status];
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-[10px] py-1 text-[12px] font-medium',
        s.bg,
      )}
    >
      <span aria-hidden="true" className={cn('inline-block h-[6px] w-[6px] rounded-full', s.dot)} />
      {s.label}
    </span>
  );
}
